import React, { useState } from 'react';
import {
  Divider,
  IconButton,
  ListItemIcon,
  listItemIconClasses,
  ListItemText,
  Menu,
  MenuItem,
  Theme,
} from '@mui/material';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import DoNotDisturb from '@mui/icons-material/DoNotDisturb';
import Edit from '@mui/icons-material/Edit';
import FormatColorText from '@mui/icons-material/FormatColorText';
import MoreVert from '@mui/icons-material/MoreVert';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import Visibility from '@mui/icons-material/Visibility';
import makeStyles from '@mui/styles/makeStyles';
import { getSignableStatus, SignableStatus } from '../../../enums/Signables';
import { SignableSchema } from '../../../definitions/schemas';
import RenameDialog from './RenameDialog';
import VoidDialog from './VoidDialog';

const useStyles = makeStyles((theme: Theme) => ({
  listItemIcon: {
    [`&.${listItemIconClasses.root}`]: {
      minWidth: theme.spacing(4.5),
    },
  },
}));

interface Props {
  isNew?: boolean;
  onDelete: () => void;
  onEdit?: () => void;
  onEditSigners?: () => void;
  onPreview?: () => void;
  onRenamed: (signable: SignableSchema) => void;
  onVoided: (signable: SignableSchema) => void;
  referrer: string;
  signable: SignableSchema;
}

const SignableMenu: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = useStyles();

  const {
    isNew,
    onDelete,
    onEdit,
    onEditSigners,
    onPreview,
    onRenamed,
    onVoided,
    referrer,
    signable,
  } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();

  const [renaming, setRenaming] = useState(false);
  const [voiding, setVoiding] = useState(false);

  const [hasStatusError, , isCompleted] = getSignableStatus(signable.status);

  const deletable = signable.status !== SignableStatus.DELETED;
  const isEditable = !(hasStatusError || isCompleted);
  const canDocumentBeEdited =
    signable.status === SignableStatus.CREATED && !signable.signableTemplateId;
  const voidable = !isNew && signable.canBeVoided;
  const canPreview = !(
    signable.status === SignableStatus.DELETED || isCompleted
  );

  const onClose = () => {
    setAnchorEl(undefined);
  };

  const onOpen = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(e.currentTarget);
  };

  const onAction = (action: () => void) => () => {
    action();
    onClose();
  };

  const onRenameClose = () => {
    setRenaming(false);
  };

  const onVoidClose = () => {
    setVoiding(false);
  };

  const handleRename = () => {
    setRenaming(true);
  };

  const handleVoid = () => {
    setVoiding(true);
  };

  return (
    <>
      <IconButton onClick={onOpen}>
        <MoreVert />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        disablePortal
        onClose={onClose}
        open={!!anchorEl}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {canPreview && onPreview && (
          <MenuItem dense onClick={onAction(onPreview)}>
            <ListItemIcon className={classes.listItemIcon}>
              <Visibility />
            </ListItemIcon>
            <ListItemText
              primary="Preview"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}

        {isEditable && canDocumentBeEdited && onEdit && (
          <MenuItem dense onClick={onAction(onEdit)}>
            <ListItemIcon className={classes.listItemIcon}>
              <Edit />
            </ListItemIcon>
            <ListItemText
              primary="Edit document"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}

        {isEditable && onEditSigners && (
          <MenuItem dense onClick={onAction(onEditSigners)}>
            <ListItemIcon className={classes.listItemIcon}>
              <PersonOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Change signers"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}

        <MenuItem dense onClick={onAction(handleRename)}>
          <ListItemIcon className={classes.listItemIcon}>
            <FormatColorText fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Rename"
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>

        {voidable && (
          <MenuItem dense onClick={onAction(handleVoid)}>
            <ListItemIcon className={classes.listItemIcon}>
              <DoNotDisturb color="error" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Void"
              primaryTypographyProps={{
                color: 'error',
                variant: 'body2',
              }}
            />
          </MenuItem>
        )}

        {voidable && <Divider />}

        {deletable && (
          <MenuItem dense onClick={onAction(onDelete)}>
            <ListItemIcon className={classes.listItemIcon}>
              <DeleteOutlined color="error" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Delete"
              primaryTypographyProps={{
                color: 'error',
                variant: 'body2',
              }}
            />
          </MenuItem>
        )}
      </Menu>

      <RenameDialog
        name={signable.name}
        onClose={onRenameClose}
        onRenamed={onRenamed}
        open={renaming}
        signableId={signable.id}
      />

      {voidable && (
        <VoidDialog
          onClose={onVoidClose}
          onVoided={onVoided}
          open={voiding}
          referrer={referrer}
          signable={signable}
        />
      )}
    </>
  );
};

export default SignableMenu;
